import { Select, TextInput } from '@octano/global-ui';
import { useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Col, Row } from 'reactstrap';

import {
  useFilteredCities,
  useFilteredCommunes,
} from '../../hooks/useFilteredCommunes';
import { useParameters } from '../../hooks/useParameters';
import { useValidations } from '../../hooks/useValidations';
import { SectionTitle } from '../text';

type ContactSectionProps = {
  prefix: string;
  fieldNames?: {
    addressStreet: string;
    addressNumber: string;
    addressExtra: string;
    region: string;
    city: string;
    commune: string;
    phone: string;
    cellphone: string;
  };
  disabled?: boolean;
};

/**
 * Para usar este componente tu formulario debe estar envuelto en su FormProvider
 * para poder acceder a la información del formulario a través de useFormContext
 * Los nombres agregados en fieldNames se relacionan a los campos que utilizaste
 * al definir tu formulario.
 */
const ContactSection = ({
  prefix,
  fieldNames = {
    addressStreet: 'addressStreet',
    addressNumber: 'addressNumber',
    addressExtra: 'addressExtra',
    region: 'region',
    city: 'city',
    commune: 'commune',
    phone: 'phone',
    cellphone: 'cellphone',
  },
  disabled: readOnly = false,
}: ContactSectionProps) => {
  const { control, watch, formState, setValue } = useFormContext();
  const { t } = useTranslation();
  const {
    msgValidations,
    validateTextNotEmpty,
    validatePhone,
  } = useValidations();
  const { regionOptions } = useParameters();

  const selectedRegion = watch(fieldNames.region);
  const filteredCommunes = useFilteredCommunes(selectedRegion);
  const filteredCities = useFilteredCities(selectedRegion);
  const isRegionTouched = formState.touchedFields[fieldNames.region];

  useEffect(() => {
    if (isRegionTouched) {
      setValue(fieldNames.commune, null);
      setValue(fieldNames.city, null);
    }
  }, [
    selectedRegion,
    setValue,
    isRegionTouched,
    fieldNames.commune,
    fieldNames.city,
  ]);

  return (
    <>
      <Row className="pt-5">
        <Col xs={12}>
          <SectionTitle text={t(`${prefix}.title`)} />
        </Col>
      </Row>
      <Row>
        <Col className="pb-3" xs={12} lg={6}>
          <TextInput
            name={fieldNames.addressStreet}
            label={t(`${prefix}.${fieldNames.addressStreet}`)}
            control={control}
            rules={{ validate: validateTextNotEmpty }}
            readOnly={readOnly}
            maxLength={250}
          />
        </Col>
        <Col className="pb-3" xs={12} lg={6}>
          <TextInput
            name={fieldNames.addressNumber}
            label={t(`${prefix}.${fieldNames.addressNumber}`)}
            control={control}
            rules={{ validate: validateTextNotEmpty }}
            readOnly={readOnly}
            maxLength={10}
          />
        </Col>
        <Col className="pb-3" xs={12} lg={4}>
          <TextInput
            name={fieldNames.addressExtra}
            label={t(`${prefix}.${fieldNames.addressExtra}`)}
            control={control}
            readOnly={readOnly}
            maxLength={10}
          />
        </Col>
        <Col className="pb-3" xs={12} lg={4}>
          <Select
            name={fieldNames.region}
            label={t(`${prefix}.${fieldNames.region}`)}
            options={regionOptions}
            control={control}
            rules={{ required: msgValidations.required }}
            disabled={readOnly}
          />
        </Col>
        <Col className="pb-3" xs={12} lg={4}>
          <Select
            name={fieldNames.city}
            label={t(`${prefix}.${fieldNames.city}`)}
            options={filteredCities}
            control={control}
            rules={{ required: msgValidations.required }}
            disabled={readOnly}
          />
        </Col>
        <Col className="pb-3" xs={12} lg={4}>
          <Select
            name={fieldNames.commune}
            label={t(`${prefix}.${fieldNames.commune}`)}
            options={filteredCommunes}
            control={control}
            rules={{ required: msgValidations.required }}
            disabled={readOnly}
          />
        </Col>
        <Col className="pb-3" xs={12} lg={4}>
          <TextInput
            name={fieldNames.phone}
            label={t(`${prefix}.${fieldNames.phone}`)}
            control={control}
            formatter="phone"
            rules={{ validate: validatePhone }}
            readOnly={readOnly}
            placeholder={t(`common.placeholder.phone`)}
            maxLength={15}
          />
        </Col>
        <Col className="pb-3" xs={12} lg={4}>
          <TextInput
            name={fieldNames.cellphone}
            label={t(`${prefix}.${fieldNames.cellphone}`)}
            control={control}
            formatter="phone"
            rules={{
              validate: {
                notEmpty: validateTextNotEmpty,
                phone: validatePhone,
              },
            }}
            readOnly={readOnly}
            placeholder={t(`common.placeholder.cellphone`)}
            maxLength={15}
          />
        </Col>
      </Row>
    </>
  );
};

export default ContactSection;
